/**
 * Header component — reads action buttons from role registry.
 */

import { auth } from '../auth';
import { api } from '../api';
import { registry } from '../roles/registry';

/**
 * Render the top header bar.
 *
 * @param el   — the #header element
 * @param user — current user object
 */
export function renderHeader(el: HTMLElement, user: any): void {
    const actions = registry.getHeaderActions();
    const actionHtml = actions.map((a: any) => `<button class="inline-flex items-center justify-center h-9 px-4 text-sm font-semibold rounded border border-border bg-white text-text-primary hover:bg-bg-secondary transition cursor-pointer" id="${a.id}">${a.label}</button>`).join('');

    el.innerHTML = `
    <div class="header flex items-center gap-4 px-5 py-3 bg-white border-b border-border sticky top-0 z-30">
        <button class="mobile-menu-btn md:hidden w-9 h-9 flex items-center justify-center text-xl cursor-pointer" id="mobile-menu-btn" aria-label="Меню">&#9776;</button>
        <h2 id="page-title" class="text-lg font-bold whitespace-nowrap">Панель управління</h2>
        <span class="flex-1"></span>
        <input class="search-input h-9 px-3 text-sm border border-border rounded bg-white w-64 max-w-xs" placeholder="Пошук користувача, номера..." id="global-search" />
        ${actionHtml}
        <button class="inline-flex items-center justify-center h-9 px-4 text-sm font-semibold rounded bg-nir-dark text-white hover:bg-nir-dark/90 transition cursor-pointer" id="btn-logout">Вийти</button>
    </div>`;

    // Logout handler
    const logoutBtn = document.getElementById('btn-logout');
    if (logoutBtn) {
        logoutBtn.addEventListener('click', () => auth.logout());
    }

    // Mobile menu button
    const mobileMenuBtn = document.getElementById('mobile-menu-btn');
    if (mobileMenuBtn) {
        mobileMenuBtn.addEventListener('click', () => {
            document.body.classList.toggle('sidebar-mobile-open');
        });
    }

    // Global search
    const searchInput = document.getElementById('global-search') as HTMLInputElement | null;
    let searchTimeout: ReturnType<typeof setTimeout> | null = null;
    let searchDropdown: HTMLDivElement | null = null;

    if (searchInput) {
        // Create dropdown
        searchDropdown = document.createElement('div');
        searchDropdown.className = 'dropdown-list';
        searchDropdown.style.cssText =
            'display:none;position:absolute;top:100%;left:0;right:0;background:var(--color-bg-primary);border:1px solid var(--color-border);max-height:300px;overflow-y:auto;z-index:200;box-shadow:var(--shadow-md);border-radius:0 0 8px 8px';
        (searchInput.parentElement as HTMLElement).style.position = 'relative';
        searchInput.parentElement!.appendChild(searchDropdown);

        searchInput.addEventListener('keydown', (e: KeyboardEvent) => {
        const items = dropdown.querySelectorAll('.search-result-item');
        if (!items.length) return;
        const active = dropdown.querySelector('.search-result-item.active');
        let idx = active ? Array.from(items).indexOf(active) : -1;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (active) active.classList.remove('active');
            idx = (idx + 1) % items.length;
            items[idx].classList.add('active');
            (items[idx] as HTMLElement).scrollIntoView({ block: 'nearest' });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (active) active.classList.remove('active');
            idx = idx <= 0 ? items.length - 1 : idx - 1;
            items[idx].classList.add('active');
            (items[idx] as HTMLElement).scrollIntoView({ block: 'nearest' });
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (active) (active as HTMLElement).click();
        }
    });

    searchInput.addEventListener('input', () => {
            if (searchTimeout) clearTimeout(searchTimeout);
            const q = searchInput.value.trim();
            if (q.length < 2) {
                searchDropdown!.style.display = 'none';
                return;
            }
            searchTimeout = setTimeout(async () => {
                try {
                    const data: any = await api.get('/users/?page_size=200');
                    const users: any[] = (data.items || data.users || (Array.isArray(data) ? data : [])).filter((u: any) => u.extension !== '11111');
                    const filtered = users
                        .filter(
                            (u: any) =>
                                (u.full_name || '').toLowerCase().includes(q.toLowerCase()) ||
                                (u.extension || '').includes(q) ||
                                (u.email || '').toLowerCase().includes(q.toLowerCase()),
                        )
                        .slice(0, 8);

                    if (!filtered.length) {
                        searchDropdown!.innerHTML =
                            '<div class="p-3 text-text-secondary text-[13px]">Нічого не знайдено</div>';
                    } else {
                        searchDropdown!.innerHTML = filtered
                            .map(
                                (u: any) => `
                            <div class="flex items-center gap-3 px-4 py-2 hover:bg-bg-secondary cursor-pointer" data-ext="${u.extension}" data-uid="${u.id}">
                                <span class="inline-block px-2 py-0.5 bg-blue-50 text-nir-blue text-xs font-mono rounded">${u.extension || '-'}</span>
                                <div class="flex-1 min-w-0">
                                    <div class="font-semibold text-[13px]">${u.full_name}</div>
                                    <div class="text-xs text-text-secondary">${u.email || ''}</div>
                                </div>
                            </div>
                        `,
                            )
                            .join('');
                    }
                    searchDropdown!.style.display = '';
                } catch (e: unknown) {
                    searchDropdown!.style.display = 'none';
                }
            }, 300);
        });

        // Click on result — navigate to forwarding for that user
        const dropdown = searchDropdown;
        dropdown.addEventListener('click', (e: MouseEvent) => {
            const row = (e.target as HTMLElement).closest('[data-ext]');
            if (row) {
                searchInput.value = '';
                dropdown.style.display = 'none';
                window.location.hash = '#/forwarding';
            }
        });

        // Close on outside click
        document.addEventListener('click', (e: MouseEvent) => {
            if (!searchInput.contains(e.target as Node) && !dropdown.contains(e.target as Node)) {
                dropdown.style.display = 'none';
            }
        });

        // Close on Escape
        searchInput.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                dropdown.style.display = 'none';
                searchInput.blur();
            }
        });
    }

    // Header action buttons — wire click handlers
    for (const action of actions) {
        const btn = document.getElementById((action as any).id);
        if (btn && (action as any).href) {
            btn.addEventListener('click', () => {
                window.location.hash = (action as any).href.replace('#', '');
                // Trigger page-specific action after navigation
                setTimeout(() => {
                    if ((action as any).id === 'btn-new-user') {
                        // Import and call create user modal dynamically
                        import('../pages/users.js').then((m: any) => {
                            if (m.openCreateUserModal) m.openCreateUserModal();
                        });
                    }
                }, 500);
            });
        }
    }
}
